import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setToken, pickToken, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/I18nContext";
import BrandLogo from "@/components/common/BrandLogo";

/** Handle Emergent Google Auth callback.
 *  User has just returned from Emergent with a session_id in the URL fragment.
 *  We exchange it for an app session via POST /api/auth/session, then land on /app. */
export default function AuthCallback() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const t = useT();
  const processed = useRef(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const run = async () => {
      // session_id is delivered in the URL fragment: #session_id=xxx
      const hash = window.location.hash || "";
      const match = hash.match(/session_id=([^&]+)/);
      const session_id = match ? decodeURIComponent(match[1]) : "";

      if (!session_id) {
        setErr(t("errors.generic"));
        setTimeout(() => nav("/login", { replace: true }), 1200);
        return;
      }

      try {
        const res = await api.post("/auth/session", { session_id });
        const tok = pickToken(res.data);
        if (tok) setToken(tok);
        // Wipe the fragment so the token isn't lingering in browser history
        window.history.replaceState({}, document.title, window.location.pathname);
        await refresh();
        // Honour an in-flight post-login redirect (e.g. from /delete-account).
        let next = "/app";
        try {
          const stored = sessionStorage.getItem("ic_post_login_redirect");
          if (stored && stored.startsWith("/") && !stored.startsWith("//")) {
            next = stored;
          }
          sessionStorage.removeItem("ic_post_login_redirect");
        } catch {}
        nav(next, { replace: true });
      } catch (e) {
        setErr(errMsg(e, t("errors.generic")));
        setTimeout(() => nav("/login", { replace: true }), 1800);
      }
    };
    run();
  }, [nav, refresh, t]);

  return (
    <div className="min-h-screen bg-cream-50 flex items-center justify-center" data-testid="auth-callback">
      <div className="text-center animate-fade-up">
        <BrandLogo size="2xl" className="mx-auto mb-4 animate-pulse" />
        <div className="font-heading text-2xl text-ink-900 tracking-tight">
          {err || t("common.loading")}
        </div>
        {!err && (
          <div className="text-sm text-ink-500 mt-2">{t("brand.subtitle")}</div>
        )}
      </div>
    </div>
  );
}
